import { getRealUrlPrefix, TypeBoundAxiosInstance } from '@m43/core';
import { Socket, StreamBroadcaster } from '@m43/sockets-mark43';
import { RecordTimingRequestDto, TracingResource } from '@mark43/cad-api';
import { UserResource } from '@mark43/rms-api';
import { StreamMap } from './stream-types';
import { getAppName } from '../app-name/app-name';
import { logDebug, logError, logInfo, logWarn } from '../monitoring/log';

const FLUSH_INTERVAL_MS = 30_000;
const BATCH_WARN_THRESHOLD = 100;

let currentUserId: number | undefined;
let tracingRecordTimingEnabled = false;

export function setSocketUserId(userId: number) {
    currentUserId = userId;
}

export function setTracingRecordTimingEnabled(enabled: boolean) {
    tracingRecordTimingEnabled = enabled;
}

export function createSocket(args: {
    apiClient: TypeBoundAxiosInstance;
    streamBroadcaster: StreamBroadcaster<StreamMap>;
}) {
    const { apiClient, streamBroadcaster } = args;
    const appName = getAppName();

    let timingBuffer: RecordTimingRequestDto[] = [];

    async function flushTimingBuffer() {
        if (timingBuffer.length === 0) return;
        if (timingBuffer.length > BATCH_WARN_THRESHOLD) {
            logWarn('tracing timing buffer exceeded threshold', {
                type: 'tracing-timing-buffer-large',
                size: timingBuffer.length,
            });
        }
        const dtos = timingBuffer;
        timingBuffer = [];
        try {
            await apiClient.request<TracingResource.RecordTiming>({
                method: 'POST',
                url: 'tracing/timing',
                data: { dtos },
            });
        } catch (e) {
            logWarn('failed to record tracing timing batch', {
                type: 'tracing-timing-flush-failed',
                size: dtos.length,
            });
        }
    }

    setInterval(flushTimingBuffer, FLUSH_INTERVAL_MS);

    const socket = new Socket({
        socketBaseUrl: `${getRealUrlPrefix().replace('http', 'ws')}/${
            appName === 'cad' ? 'cad' : 'fr'
        }-sync-next/api/entity/`,
        fetchSocketToken: async (): Promise<string> => {
            const response = await apiClient.request<UserResource.GenerateRealtimeToken>({
                method: 'GET',
                url: 'user/realtime',
                service: 'RMS',
            });
            return response.data.data!;
        },
        streamBroadcaster,
        // if the socket has not received a message in this many seconds it shifts into a 'FATAL' status
        messageTimeoutThreshold: 22000,
        messageTimeoutCheckFrequency: 3000,
        onMessage(message, metadata) {
            if (message.streamName === 'gps') return;
            if (
                tracingRecordTimingEnabled &&
                metadata &&
                metadata.originatingTraceId &&
                currentUserId !== undefined &&
                metadata.originatingUserId === currentUserId
            ) {
                timingBuffer.push({
                    startMillis: metadata.requestInitiatedMillis,
                    endMillis: Date.now(),
                    websocketBroadcastedMillis: metadata.websocketBroadcastedMillis,
                    isOriginatingUser: true,
                    operationName: metadata.operationName,
                    endpoint: metadata.endpoint,
                    publisherSource: metadata.publisherSource,
                    clientActionId: metadata.clientActionId,
                    traceMap: metadata.traceMap,
                    errored: false,
                });
            }
        },
        onSocketStatusChange(status) {
            logInfo(`socket status changed to ${status.status}`);
        },
        onWarning(warning) {
            logWarn(warning);
        },
        onFatal(source, eventData) {
            logError(new Error('socket fatal error'), {
                type: `socket_fatal.${source}`,
                eventData: JSON.stringify(eventData),
            });
        },
        onOutOfOrderMessage({ streamName, messagePosition }) {
            logDebug('Socket received message with unexpected last message position', {
                type: 'out-of-order-socket-message',
                streamName: String(streamName),
                messagePosition,
            });
        },
    });
    socket.setApplicationName(appName);
    return socket;
}
